import { gql }                                  from 'apollo-boost';
import { Uuid }                                 from '../types';
import { getFieldWithProjectVersionIdAccessor } from "@src/util";
import { ActivityQueries }                      from "@src/queries/ActivityQueries";


export const fragments = {

    SUBOBJECTIVES: {
        QUERY_RETURN: `id name displayName`
    },

    SUBOBJECTIVE: {
        QUERY_RETURN:    `id name displayName description objectiveId`,
        MUTATION_RETURN: `id name displayName description objectiveId`,
        EDIT_FIELDS:     `
			id
			name
			displayName
			description
			refIdCode
			objectiveId
			targetIntakeQuestionId
			subCategoryId
			applicableStartYear
			milestoneId
			projectTimingMilestoneOffsetMonths
			conditions {
				id
				conditionType
				value
			}
			ordinal
		`
    },

    PROJECT_SUBOBJECTIVES: {
        QUERY_RETURN: ( projectId?: Uuid, projectVersionId = null ) => `
			id
			projectId
			startTimeUtc
			endTimeUtc
			notes
			subObjective{
				id
				ordinal
				objective{
					id
					displayName
					description
					ordinal
				}
				targetIntakeQuestionId
				
				${ getFieldWithProjectVersionIdAccessor( projectVersionId, 'name' ) }
				${ getFieldWithProjectVersionIdAccessor( projectVersionId, 'displayName' ) }
                ${ getFieldWithProjectVersionIdAccessor( projectVersionId, 'description' ) }
                ${ getFieldWithProjectVersionIdAccessor( projectVersionId, 'categoryId' ) }
                ${ getFieldWithProjectVersionIdAccessor( projectVersionId, 'category' ) } { 
                    id
                    displayName
                    description
                    ordinal
                }
                ${ getFieldWithProjectVersionIdAccessor( projectVersionId, 'subCategoryId' ) }
                ${ getFieldWithProjectVersionIdAccessor( projectVersionId, 'subCategory' ) } {
                    id
					displayName
					description
					ordinal
                }
				applicableStartYear
				milestoneId
				projectTimingMilestoneOffsetMonths
				${ projectId ? `isRecommended( projectId: "${ projectId }" )` : `` }
			}
		`,

        MUTATION_RETURN: `
			id
			startTimeUtc
			endTimeUtc
			subobjective{ id displayName }
		`,

        PROGRESSION_QUERY_RETURN: ( versionId = null ) => `
			id
			startTimeUtc
			endTimeUtc
			staticProjectActivities {
				id
				displayName
				startDate
				endDate
				completionState
			}
			subObjective{
				id
				displayName
			}
		`,

        PROJECT_SUBOBJECTIVE_TIMING_FIELDS: `
			startTimeCalculatedUtc
			endTimeCalculatedUtc
		`
    },
};

export const queries = {

    GET_SUBOBJECTIVES: ( returnQueryString = fragments.SUBOBJECTIVES.QUERY_RETURN ) => {

        const query = `
        query GetSubObjectives( 
            $input: SubObjectiveSearchQueryInput! = {}, 
            $order: [SubObjectiveSortInput!], 
            $where: SubObjectiveFilterInput 
        ){
            subObjectives( 
                input: $input, 
                order: $order, 
                where: $where
            ) {
                ${ returnQueryString }
            }
        }`

        return gql( query );
    },

    GET_SUBOBJECTIVE: ( returnQueryString = fragments.SUBOBJECTIVE.QUERY_RETURN ) => {

        const query = `
        query GetSubObjective( $id: Uuid! ){
            subObjective( id: $id ) {
                ${ returnQueryString }
            }
        }`;

        return gql( query );
    },

    GET_PROJECT_SUBOBJECTIVES: ( returnQueryString = fragments.PROJECT_SUBOBJECTIVES.QUERY_RETURN() ) => {

        const query = `
            query GetProjectSubObjectives(
                $order:     [StaticProjectSubObjectiveSortInput!],
                $where:     StaticProjectSubObjectiveFilterInput,
                $projectVersionId: Uuid!,
            ){  
                staticProjectSubObjectives(
                    order:              $order,
                    where:              $where,
                    projectVersionId: $projectVersionId
                ) {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );

    },

    GET_PROJECT_SUBOBJECTIVE_PROGRESS: ( projectVersionId = null ) => {

        const query = `
        query GetProjectSubObjectiveProgress(
            $order:     [ StaticProjectSubObjectiveSortInput! ],
            $where:     StaticProjectSubObjectiveFilterInput,
            $projectVersionId: Uuid!,
        ){

            staticProjectSubObjectives(
                order:              $order,
                where:              $where,
                projectVersionId: $projectVersionId,
            ) {
                ${ fragments.PROJECT_SUBOBJECTIVES.PROGRESSION_QUERY_RETURN( projectVersionId ) }
            }
        }`;

        return gql( query );
    },

    GET_DECLINED_PROJECT_SUBOBJECTIVES: ( returnQueryString = fragments.SUBOBJECTIVES.QUERY_RETURN ) => {

        const query = `
        query GetDeclinedProjectSubObjectives( $projectVersionId: Uuid! ){
            missingSubObjectivesForProjectVersion(
                projectVersionId: $projectVersionId
            ) {
                ${ returnQueryString }
            }
        }`;

        return gql( query );
    }
};

export const mutations = {

    CREATE_SUBOBJECTIVE: ( returnQueryString = fragments.SUBOBJECTIVE.MUTATION_RETURN ) => {

        const query = `
        mutation CreateSubObjective( $input: SubObjectiveCreateCommandInput! ){
            createSubObjective( input: $input ) {
                ${ returnQueryString }
            }
        }`;

        return gql( query );
    },

    UPDATE_SUBOBJECTIVE: ( returnQueryString = fragments.SUBOBJECTIVE.MUTATION_RETURN ) => {

        const query = `
        mutation UpdateSubObjective( $input: SubObjectiveUpdateCommandInput! ){
            updateSubObjective( input: $input ) {
                ${ returnQueryString }
            }
        }`;

        return gql( query );
    },

    DELETE_SUBOBJECTIVE: () => {

        const query = `
            mutation DeleteSubObjective( $input: SubObjectiveDeleteCommandInput! ){
                deleteSubObjective( input: $input )
            }`;

        return gql( query );
    },

    UPDATE_SUBOBJECTIVE_ORDER: ( returnQueryString = fragments.SUBOBJECTIVE.QUERY_RETURN ) => {

        const query = `
            mutation UpdateSubObjectiveOrder( $input: SubObjectiveUpdateOrdinalsCommandInput! ){
                updateSubObjectiveOrdinals( input: $input ) {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    SAVE_PROJECT_OBJECTIVE: ( returnQueryString = fragments.PROJECT_SUBOBJECTIVES.QUERY_RETURN() ) => {

        const query = `
            mutation SaveProjectSubObjective( $input: ProjectSubObjectiveToggleCommandInput! ){
                toggleProjectSubObjective( input: $input ) {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    OVERRIDE_PROJECT_OBJECTIVE_TIMING: ( returnQueryString = fragments.SUBOBJECTIVE.MUTATION_RETURN ) => {

        const query = `
            mutation OverrideProjectSubObjectiveTiming( $input: ProjectSubObjectiveOverrideTimingCommandInput! ){
                overrideProjectSubObjectiveTiming( input: $input ) {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    UPDATE_PROJECT_SUBOBJECTIVES: ( returnQueryString = fragments.PROJECT_SUBOBJECTIVES.MUTATION_RETURN ) => {

        const query = `
            mutation UpdateProjectSubObjectives( $input: ProjectSubObjectiveUpdateCommandInput! ){
                updateProjectSubObjectives( input: $input )  {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    ADD_PROJECT_VERSION_SUBOBJECTIVES: (
                                           returnQueryStringActivities   = ActivityQueries.fragments.STATIC_PROJECT_ACTIVITY.QUERY_RETURN(),
                                           returnQueryStringDependencies = ActivityQueries.fragments.STATIC_PROJECT_ACTIVITY_DEPENDENCIES.QUERY_RETURN()
                                       ) => {

        const query = `
            mutation UpdateProjectSubObjectives( $input: ProjectVersionAddSubObjectivesCommandInput! ){
                addProjectVersionSubObjectives( input: $input )  {
                    staticProjectActivities{
                        ${ returnQueryStringActivities }
                    }
                    ganttDependencies{
                        ${ returnQueryStringDependencies }
                    }
                }
            }`;

        return gql( query );
    },

};

export const functions = {

}

export const ObjectiveQueries = {
    fragments,
    queries,
    mutations,
    functions,
};
